import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
  Modal,
  Backdrop,
  Fade,
  Card,
  CardContent,
  Grid,
  Tooltip,
  Divider,
  Paper,
  useTheme,
  useMediaQuery,
  alpha,
  Stepper,
  Step,
  StepLabel,
  Alert,
  AlertTitle,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useSelector, useDispatch } from 'react-redux';
import AppointmentAvailabilityChecker from '../components/AppointmentAvailabilityChecker';
import AppointmentConflictResolver from '../components/AppointmentConflictResolver';
import {
  fetchClinics,
  fetchDoctors,
  fetchAvailability,
  bookAppointment,
  resetBookingState,
} from '../features/booking/bookingSlice';
import { fetchPublicDoctorProfile } from '../features/doctors/publicDoctorSlice';
import publicAxios from '../api/publicAxios';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DateTime } from "luxon";
import { getBookingData, saveBookingData, clearBookingData, shouldRestoreBookingData } from '../utils/bookingStorage';

// Styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: 16,
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
  background: theme.palette.background.paper,
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 4,
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
    transition: theme.transitions.create(['box-shadow', 'border-color']),
    '&:hover': {
      borderColor: theme.palette.primary.main,
    },
    '&.Mui-focused': {
      boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
    },
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: 12,
  padding: theme.spacing(1.5),
  textTransform: 'none',
  fontSize: '1.1rem',
  fontWeight: 600,
  boxShadow: 'none',
  '&:hover': {
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  },
}));

const StyledTimeSlot = styled(Button)(({ theme }) => ({
  borderRadius: 8,
  padding: theme.spacing(1, 2),
  margin: theme.spacing(0.5),
  textTransform: 'none',
  fontSize: '0.9rem',
  fontWeight: 500,
  transition: 'all 0.2s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
  },
  '&.selected': {
    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
    color: 'white',
    '&:hover': {
      background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`,
    },
  },
}));

const StyledCalendar = styled(Calendar)(({ theme }) => ({
  '.react-calendar': {
    border: 'none',
    borderRadius: 16,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
    padding: theme.spacing(2),
    fontFamily: theme.typography.fontFamily,
  },
  '.react-calendar__tile': {
    borderRadius: 8,
    padding: '1em 0.5em',
    '&:enabled:hover': {
      background: alpha(theme.palette.primary.main, 0.1),
    },
    '&--active': {
      background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main}) !important`,
      color: 'white',
    },
  },
  '.react-calendar__navigation button': {
    borderRadius: 8,
    '&:enabled:hover': {
      background: alpha(theme.palette.primary.main, 0.1),
    },
  },
}));

const StyledModal = styled(Modal)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& .MuiPaper-root': {
    borderRadius: 16,
    padding: theme.spacing(4),
    maxWidth: 400,
    width: '90%',
  },
}));

const BookAppointment = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useSelector((state) => state.auth);
  const { clinics, doctors, availability, loading, error, bookingSuccess } = useSelector(
    (state) => state.booking
  );

  let slot_duration = 30;

  if (availability.available_slots && availability.available_slots.length > 0) {
    console.log(availability);
    slot_duration = availability.slot_duration;
  }

  let available_slots = [];

  if (availability.available_slots && availability.available_slots.length > 0) {
    available_slots = availability.available_slots;
  }

  const [selectedClinic, setSelectedClinic] = useState('');
  const [selectedDoctor, setSelectedDoctor] = useState('');
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState('');
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [currentDoctorDetails, setCurrentDoctorDetails] = useState(null);

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const getSteps = () => {
    return ['Select Clinic', 'Choose Doctor', 'Pick Date & Time', 'Confirm'];
  };

  const [activeStep, setActiveStep] = useState(0);
  const steps = getSteps();

  useEffect(() => {
    // Check for doctor ID in URL query parameter
    const doctorIdFromUrl = searchParams.get('doctor');
    
    // Clear old booking data if starting fresh booking with different doctor
    if (doctorIdFromUrl) {
      const shouldRestore = shouldRestoreBookingData(doctorIdFromUrl);
      if (!shouldRestore) {
        clearBookingData();
      }
    }
    
    // Get saved booking data using utility function (includes timestamp validation)
    const savedData = getBookingData();
    
    // Only restore if data is valid and matches current doctor (if specified)
    if (savedData && savedData.selectedDoctor && shouldRestoreBookingData(doctorIdFromUrl || savedData.selectedDoctor)) {
      setIsInitialLoading(true);
      
      toast.success(
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Welcome back! 👋
          </Typography>
          <Typography variant="body2">
            We've restored your booking details. {user ? 'You can proceed to confirm your appointment.' : 'Please log in to complete your booking.'}
          </Typography>
        </Box>,
        {
          position: "bottom-center",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          style: {
            background: theme.palette.background.paper,
            color: theme.palette.text.primary,
            borderRadius: '12px',
            boxShadow: theme.shadows[3],
          },
        }
      );

      setSelectedDoctor(savedData.selectedDoctor || '');
      setSelectedClinic(savedData.selectedClinic || '');
      
      // Restore saved booking data
      if (savedData.selectedDate) {
        // Parse saved date - handle different formats (YYYY-MM-DD or ISO string)
        try {
          let parsedDate;
          if (savedData.selectedDate.includes('T')) {
            parsedDate = new Date(savedData.selectedDate);
          } else if (savedData.selectedDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
            // YYYY-MM-DD format
            parsedDate = new Date(savedData.selectedDate + 'T00:00:00');
          } else {
            parsedDate = new Date(savedData.selectedDate);
          }
          if (!isNaN(parsedDate.getTime())) {
            setSelectedDate(parsedDate);
          }
        } catch (e) {
          console.warn('Failed to parse saved date:', e);
        }
      }
      if (savedData.selectedSlot) {
        setSelectedSlot(savedData.selectedSlot);
      }
      
      // If date and slot are restored, advance to confirmation step
      if (savedData.selectedDate && savedData.selectedSlot) {
        setActiveStep(3); // Confirmation step
      } else if (savedData.selectedDoctor) {
        setActiveStep(1); // Doctor selection step (doctor already selected)
      }
      
      // Fetch availability for saved date or today
      const dateToFetch = savedData.selectedDate || DateTime.now().toFormat('yyyy-MM-dd');
      
      Promise.all([
        dispatch(fetchClinics()),
        dispatch(fetchDoctors(savedData.selectedClinic || null)),
        savedData.selectedDoctor ? dispatch(fetchAvailability({ 
          doctorId: savedData.selectedDoctor,
          date: dateToFetch
        })) : Promise.resolve()
      ]).finally(() => {
        setIsInitialLoading(false);
      });
    } else if (doctorIdFromUrl) {
      // Handle doctor ID from URL query parameter - fresh booking flow
      setIsInitialLoading(true);
      
      // Clear any old booking data for different doctors
      const savedData = getBookingData();
      if (savedData && savedData.selectedDoctor && savedData.selectedDoctor !== doctorIdFromUrl) {
        clearBookingData();
      }
      
      setSelectedDoctor(doctorIdFromUrl);
      // Advance to date/time selection step (step 2) since doctor is already selected
      setActiveStep(2);
      
      // Fetch specific doctor profile first to get full details, then fetch clinics, doctors list, and availability
      const fetchDoctorProfile = async () => {
        try {
          const response = await publicAxios.get(`/doctors/${doctorIdFromUrl}/profile/`);
          const doctorData = response.data;
          setCurrentDoctorDetails(doctorData);
          
          // If doctor has clinic, pre-select it
          if (doctorData.clinic?.id || doctorData.clinic_id || doctorData.clinicId) {
            const clinicId = doctorData.clinic?.id || doctorData.clinic_id || doctorData.clinicId;
            setSelectedClinic(clinicId);
          }
        } catch (error) {
          console.error('Error fetching doctor profile:', error);
          // Continue even if profile fetch fails
        }
      };
      
      Promise.all([
        fetchDoctorProfile(),
        dispatch(fetchClinics()),
        dispatch(fetchDoctors(null)), // Fetch all doctors to populate dropdown
        dispatch(fetchAvailability({ 
          doctorId: doctorIdFromUrl,
          date: DateTime.now().toFormat('yyyy-MM-dd')
        }))
      ]).finally(() => {
        setIsInitialLoading(false);
        
        // After doctors are loaded, check if selected doctor has a clinic and pre-fill it
        // This happens in a separate effect below
      });
    } else {
      // Load clinics and all available doctors for public booking
      setIsInitialLoading(true);
      // Clear old booking data when starting completely fresh
      clearBookingData();
      
      Promise.all([
        dispatch(fetchClinics()),
        dispatch(fetchDoctors(null)) // Fetch all doctors for public booking
      ]).finally(() => {
        setIsInitialLoading(false);
      });
    }
  }, [dispatch, searchParams]);

  // Helper function to normalize IDs for comparison
  const normalizeId = useCallback((id) => {
    if (id === null || id === undefined || id === '') return null;
    return String(id);
  }, []);

  // Helper function to check if two IDs match (handles empty strings)
  const idsMatch = useCallback((id1, id2) => {
    // Both must be truthy to match (empty strings don't match)
    if (!id1 && !id2) return true; // Both empty
    if (!id1 || !id2) return false; // One is empty, other is not
    return normalizeId(id1) === normalizeId(id2);
  }, [normalizeId]);

  // Helper function to get doctor details from either currentDoctorDetails or doctors list
  const getDoctorDetails = useCallback(() => {
    if (!selectedDoctor) return null;
    
    // Check currentDoctorDetails first
    if (currentDoctorDetails) {
      const docId = currentDoctorDetails.id || currentDoctorDetails.uuid || currentDoctorDetails.doctor_id;
      if (idsMatch(docId, selectedDoctor)) {
        return currentDoctorDetails;
      }
    }
    
    // Fallback to doctors list
    if (doctors.length > 0) {
      return doctors.find(d => {
        const docId = d.id || d.uuid || d.doctor_id;
        return idsMatch(docId, selectedDoctor);
      });
    }
    
    return null;
  }, [selectedDoctor, doctors, currentDoctorDetails, idsMatch]);
  

  useEffect(() => {
    // Fetch doctors - either for selected clinic or all doctors if no clinic selected
    // Always fetch based on selected clinic
    // Note: If we have currentDoctorDetails from URL, it will be shown separately in dropdown
    dispatch(fetchDoctors(selectedClinic || null));
  }, [dispatch, selectedClinic]);

  // Auto-select clinic if doctor has associated clinic
  useEffect(() => {
    const doctor = getDoctorDetails();
    
    if (doctor && selectedDoctor) {
      // Check if doctor has clinic information
      const doctorClinicId = doctor.clinic?.id || doctor.clinic_id || doctor.clinicId;
      const doctorClinic = doctor.clinic;
      
      if (doctorClinicId && !idsMatch(selectedClinic, doctorClinicId)) {
        // Doctor has a clinic - pre-fill it (find the actual clinic to get correct ID)
        const clinic = clinics.find(c => idsMatch(c.id, doctorClinicId));
        const actualClinicId = clinic ? clinic.id : doctorClinicId;
        
        if (actualClinicId) {
          setSelectedClinic(actualClinicId);
          
          // Also update saved booking data
          const currentBookingData = getBookingData() || {};
          saveBookingData({
            ...currentBookingData,
            selectedDoctor,
            selectedClinic: actualClinicId,
          });
          
          console.log('✅ Pre-filled clinic for doctor:', {
            doctorId: selectedDoctor,
            doctorName: doctor.name || doctor.full_name || doctor.user?.name || doctor.title,
            clinicId: actualClinicId,
            clinicName: doctorClinic?.name || doctorClinic?.clinic_name,
          });
        }
      } else if (!doctorClinicId && selectedClinic) {
        // Doctor doesn't have a clinic but one was previously selected
        // Only clear if it was auto-selected (not user-selected)
        // We'll keep it for now to preserve user choice
      }
    } else if (selectedDoctor) {
      // Doctor not found - might still be loading
      console.log('Doctor not found yet, might still be loading...', {
        selectedDoctor,
        doctorsCount: doctors.length,
        hasCurrentDoctorDetails: !!currentDoctorDetails,
      });
    }
  }, [selectedDoctor, doctors, selectedClinic, currentDoctorDetails, getDoctorDetails, clinics, idsMatch]);

  useEffect(() => {
    if (selectedDoctor && selectedDate) {
      // Format date consistently as YYYY-MM-DD
      const formattedDate = selectedDate instanceof Date
        ? selectedDate.toISOString().split('T')[0]
        : (typeof selectedDate === 'string'
          ? selectedDate
          : DateTime.fromJSDate(selectedDate).toFormat('yyyy-MM-dd'));
      
      dispatch(fetchAvailability({ 
        doctorId: selectedDoctor, 
        date: formattedDate 
      }));
    }
  }, [dispatch, selectedDoctor, selectedDate]);

  useEffect(() => {
    if (bookingSuccess) {
      toast.success(bookingSuccess);
      dispatch(resetBookingState());
    }
    if (error) {
      // Error handling is done in proceedWithBooking, but show generic error here if needed
      if (typeof error === 'string' && error.length > 0) {
        // toast.error(error); // Commented to avoid duplicate error messages
      }
    }
  }, [bookingSuccess, error, dispatch]);

  // Check if user logs in after being redirected from booking
  useEffect(() => {
    if (user && isLoginModalOpen && selectedDoctor && selectedDate && selectedSlot) {
      // User just logged in - proceed with booking
      setIsLoginModalOpen(false);
      // Small delay to ensure state is updated
      setTimeout(() => {
        proceedWithBooking();
      }, 100);
    }
  }, [user, isLoginModalOpen, selectedDoctor, selectedDate, selectedSlot]); // Note: proceedWithBooking is not in deps to avoid issues

  const handleBooking = () => {
    // Validate all required fields
    if (!selectedDoctor) {
      toast.error('Please select a doctor');
      setActiveStep(1);
      return;
    }
    
    if (!selectedDate) {
      toast.error('Please select a date');
      setActiveStep(2);
      return;
    }
    
    if (!selectedSlot) {
      toast.error('Please select a time slot');
      setActiveStep(2);
      return;
    }

    // Format date consistently for storage
    const formattedDate = selectedDate instanceof Date
      ? selectedDate.toISOString().split('T')[0]
      : (typeof selectedDate === 'string'
        ? selectedDate
        : DateTime.fromJSDate(selectedDate).toFormat('yyyy-MM-dd'));

    const doctorDetails = getDoctorDetails();
    const bookingData = {
      selectedClinic,
      selectedDoctor,
      selectedDate: formattedDate,
      selectedSlot,
      doctorName: doctorDetails?.user?.name || 
                  doctorDetails?.full_name || 
                  doctorDetails?.name || 
                  doctorDetails?.title ||
                  `Doctor ${selectedDoctor}`,
      clinicName: selectedClinic ? (clinics.find(c => c.id === selectedClinic)?.clinic_name || 
                                     clinics.find(c => c.id === selectedClinic)?.name) : null,
    };

    // Use utility function to save with timestamp
    saveBookingData(bookingData);

    if (!user) {
      setIsLoginModalOpen(true);
      return;
    }

    proceedWithBooking();
  };

  const proceedWithBooking = () => {
    if (!selectedDate || !selectedSlot || !selectedDoctor) {
      toast.error('Please complete all booking details');
      return;
    }

    const convertTo24Hour = (time) => {
      if (!time) return null;
      
      // Handle formats like "09:00 AM", "9:00 AM", "09:00AM", "9:00AM"
      const trimmedTime = time.trim();
      const hasPM = trimmedTime.toUpperCase().includes('PM');
      const hasAM = trimmedTime.toUpperCase().includes('AM');
      
      // Extract hour and minute
      const timeMatch = trimmedTime.match(/(\d{1,2}):(\d{2})/);
      if (!timeMatch) {
        throw new Error('Invalid time format');
      }
      
      let hours24 = parseInt(timeMatch[1], 10);
      const minutes = timeMatch[2];
      
      // Convert to 24-hour format
      if (hasPM && hours24 !== 12) {
        hours24 += 12;
      } else if (hasAM && hours24 === 12) {
        hours24 = 0;
      }
      
      return `${hours24.toString().padStart(2, '0')}:${minutes}`;
    };

    try {
      const timeIn24HourFormat = convertTo24Hour(selectedSlot);
      
      if (!timeIn24HourFormat) {
        toast.error('Invalid time format. Please select a valid time slot.');
        return;
      }

      // Format date as YYYY-MM-DD (ISO format)
      const formattedDate = selectedDate instanceof Date 
        ? selectedDate.toISOString().split('T')[0]
        : (typeof selectedDate === 'string' 
          ? selectedDate 
          : DateTime.fromJSDate(selectedDate).toFormat('yyyy-MM-dd'));

      dispatch(
        bookAppointment({
          doctorId: selectedDoctor,
          clinicId: selectedClinic || null,
          scheduledDate: formattedDate,
          scheduledTime: timeIn24HourFormat,
        })
      ).then((result) => {
        if (result.meta.requestStatus === 'fulfilled') {
          // Clear booking data on success
          clearBookingData();
          toast.success(
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Appointment Booked Successfully! ✅
              </Typography>
              <Typography variant="body2">
                Redirecting to your dashboard...
              </Typography>
            </Box>,
            {
              position: "bottom-center",
              autoClose: 3000,
            }
          );
          setTimeout(() => {
            navigate(`/${user.role}-dashboard`);
          }, 2000);
        } else if (result.meta.requestStatus === 'rejected') {
          // Handle booking conflicts and errors
          const errorPayload = result.payload;
          const errorMessage = errorPayload?.error || errorPayload?.detail || errorPayload?.message || 'Booking failed';
          
          if (errorMessage.includes('conflict') || errorMessage.includes('already booked') || errorMessage.includes('not available')) {
            toast.error('This time slot is no longer available. Please select another time.', {
              autoClose: 8000,
            });
            // Refresh availability to show updated slots
            const dateForRefresh = selectedDate instanceof Date
              ? selectedDate.toISOString().split('T')[0]
              : (typeof selectedDate === 'string'
                ? selectedDate
                : DateTime.fromJSDate(selectedDate).toFormat('yyyy-MM-dd'));
            dispatch(fetchAvailability({ 
              doctorId: selectedDoctor, 
              date: dateForRefresh
            }));
            // Go back to date/time selection
            setActiveStep(2);
          } else if (errorMessage.includes('past')) {
            toast.error('Cannot book appointments in the past. Please select a future date and time.');
            setActiveStep(2);
          } else if (errorMessage.includes('authenticated') || errorMessage.includes('login')) {
            setIsLoginModalOpen(true);
          } else {
            toast.error(`Booking failed: ${errorMessage}`, { autoClose: 6000 });
          }
        }
      }).catch((error) => {
        console.error('Booking error:', error);
        toast.error('An unexpected error occurred. Please try again.');
      });
    } catch (error) {
      console.error('Time conversion error:', error);
      toast.error('Invalid time format. Please select a valid time slot.');
    }
  };

  const handleRedirect = (path) => {
    setIsLoginModalOpen(false);
    navigate(path);
  };

  const handleNext = () => {
    // Step 0: Clinic selection is optional - user can skip
    if (activeStep === 1 && !selectedDoctor) {
      toast.error('Please select a doctor');
      return;
    }
    if (activeStep === 2 && (!selectedDate || !selectedSlot)) {
      toast.error('Please select both date and time');
      return;
    }
    if (activeStep === steps.length - 1) {
      handleBooking();
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  if (isInitialLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          gap: 2,
        }}
      >
        <CircularProgress size={40} />
        <Typography variant="h6" color="text.secondary">
          Setting up your appointment...
        </Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: { xs: 2, sm: 4, md: 6 }, mb: 4 }}>
      <Fade in timeout={1000}>
        <Box>
          <Typography 
            variant={isMobile ? 'h5' : 'h4'} 
            gutterBottom 
            align="center"
            sx={{
              fontWeight: 700,
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 4
            }}
          >
            Book Your Appointment 🗓️
          </Typography>

          <StyledPaper elevation={0}>
            <Stepper 
              activeStep={activeStep} 
              alternativeLabel={!isMobile}
              orientation={isMobile ? 'vertical' : 'horizontal'}
              sx={{ mb: 4 }}
            >
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {/* Error Display */}
            {error && (
              <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                <AlertTitle>Error</AlertTitle>
                {typeof error === 'string' ? error : 'An error occurred. Please try again.'}
              </Alert>
            )}

            <Box sx={{ mt: 4 }}>
              <Fade in timeout={500}>
                <Box>
                  {activeStep === 0 && (
                    <StyledTextField
                      select
                      label="Select Clinic (Optional)"
                      value={selectedClinic || ''}
                      onChange={(e) => {
                        const newClinicId = e.target.value || '';
                        setSelectedClinic(newClinicId);
                        
                        // If clinic changes, reset doctor selection unless doctor belongs to new clinic
                        if (selectedDoctor && newClinicId !== selectedClinic) {
                          const doctor = getDoctorDetails();
                          const doctorClinicId = doctor?.clinic?.id || doctor?.clinic_id || doctor?.clinicId;
                          
                          // Only reset doctor if it doesn't belong to new clinic
                          if (newClinicId && String(doctorClinicId) !== String(newClinicId) && doctorClinicId !== newClinicId) {
                            setSelectedDoctor('');
                            setCurrentDoctorDetails(null);
                            setSelectedDate(null);
                            setSelectedSlot('');
                          }
                        }
                      }}
                      fullWidth
                      sx={{ mb: 3 }}
                      helperText="You can select a clinic or choose 'Independent' to see all available doctors"
                    >
                      <MenuItem value="">Independent (All Doctors)</MenuItem>
                      {loading && clinics.length === 0 ? (
                        <MenuItem disabled>
                          <CircularProgress size={20} sx={{ mr: 1 }} />
                          Loading clinics...
                        </MenuItem>
                      ) : clinics.length === 0 && !loading ? (
                        <MenuItem disabled>
                          No clinics available
                        </MenuItem>
                      ) : (
                        clinics.map((clinic) => (
                          <MenuItem key={clinic.id} value={clinic.id}>
                            {clinic.clinic_name || clinic.name || `Clinic ${clinic.id}`}
                          </MenuItem>
                        ))
                      )}
                    </StyledTextField>
                  )}

                  {activeStep === 1 && (
                    <Box>
                      <StyledTextField
                        select
                        label="Select Doctor"
                        value={selectedDoctor || ''}
                        onChange={(e) => {
                          const newDoctorId = e.target.value || '';
                          
                          if (!newDoctorId) {
                            setSelectedDoctor('');
                            setCurrentDoctorDetails(null);
                            setSelectedDate(null);
                            setSelectedSlot('');
                            return;
                          }
                          
                          setSelectedDoctor(newDoctorId);
                          
                          // Reset date/time when doctor changes
                          if (newDoctorId !== selectedDoctor) {
                            setSelectedDate(null);
                            setSelectedSlot('');
                            
                            // Clear currentDoctorDetails if selecting different doctor
                            if (currentDoctorDetails) {
                              const currentDocId = currentDoctorDetails.id || currentDoctorDetails.uuid || currentDoctorDetails.doctor_id;
                              if (String(currentDocId) !== String(newDoctorId) && currentDocId !== newDoctorId) {
                                setCurrentDoctorDetails(null);
                              }
                            }
                          }
                        }}
                        fullWidth
                        disabled={(!doctors.length && !currentDoctorDetails) && loading}
                        helperText={
                          (!doctors.length && !currentDoctorDetails) && !loading 
                            ? "No doctors available. Please select a clinic or try again later." 
                            : selectedDoctor && !getDoctorDetails() && !loading
                            ? "Selected doctor not available. Please choose another."
                            : selectedDoctor && currentDoctorDetails 
                            ? "Doctor pre-selected from booking" 
                            : ""
                        }
                        error={selectedDoctor && !getDoctorDetails() && !loading && doctors.length > 0}
                      >
                        {loading && !currentDoctorDetails ? (
                          <MenuItem disabled>
                            <CircularProgress size={20} sx={{ mr: 1 }} />
                            Loading doctors...
                          </MenuItem>
                        ) : (!doctors.length && !currentDoctorDetails) ? (
                          <MenuItem disabled>
                            No doctors available
                          </MenuItem>
                        ) : (
                          <>
                            {/* Always show current doctor first if available (for direct booking from URL) */}
                            {selectedDoctor && currentDoctorDetails && (() => {
                              const currentDocId = currentDoctorDetails.id || currentDoctorDetails.uuid || currentDoctorDetails.doctor_id;
                              const selectedStr = String(selectedDoctor);
                              const currentStr = String(currentDocId || selectedDoctor);
                              
                              // Only show if IDs match
                              if (selectedStr === currentStr || currentDocId === selectedDoctor) {
                                const doctorName = currentDoctorDetails.user?.name || 
                                                  currentDoctorDetails.full_name || 
                                                  currentDoctorDetails.name || 
                                                  currentDoctorDetails.title;
                                
                                return (
                                  <MenuItem key={`current-${selectedDoctor}`} value={selectedDoctor}>
                                    {doctorName || `Doctor ${selectedDoctor}`}
                                    {currentDoctorDetails.specialization && ` - ${currentDoctorDetails.specialization}`}
                                    {' (Pre-selected)'}
                                  </MenuItem>
                                );
                              }
                              return null;
                            })()}
                            {/* Show all doctors from list, but skip if already shown above */}
                            {doctors.map((doctor) => {
                              const doctorId = doctor.id || doctor.uuid || doctor.doctor_id;
                              
                              // Skip if this doctor is already shown as currentDoctorDetails
                              if (selectedDoctor && currentDoctorDetails) {
                                const currentDocId = currentDoctorDetails.id || currentDoctorDetails.uuid || currentDoctorDetails.doctor_id;
                                const selectedStr = String(selectedDoctor);
                                const doctorIdStr = String(doctorId);
                                const currentStr = String(currentDocId || selectedDoctor);
                                
                                // Skip if this doctor matches the selected doctor
                                if (doctorIdStr === selectedStr || doctorIdStr === currentStr || doctorId === selectedDoctor || doctorId === currentDocId) {
                                  return null;
                                }
                              }
                              
                              const doctorName = doctor.user?.name || doctor.full_name || doctor.name || doctor.title;
                              
                              return (
                                <MenuItem key={doctorId} value={doctorId}>
                                  {doctorName || `Doctor ${doctorId}`}
                                  {doctor.specialization && ` - ${doctor.specialization}`}
                                </MenuItem>
                              );
                            }).filter(Boolean)}
                          </>
                        )}
                      </StyledTextField>
                      
                      {/* Show pre-selected doctor info if doctor is already selected from URL */}
                      {selectedDoctor && (
                        <Alert severity="info" sx={{ mt: 2 }}>
                          <Typography variant="body2">
                            Booking appointment with:{' '}
                            <strong>
                              {(() => {
                                const doctor = getDoctorDetails();
                                return doctor?.user?.name || doctor?.full_name || doctor?.name || doctor?.title || `Doctor ${selectedDoctor}`;
                              })()}
                            </strong>
                            {selectedClinic && (() => {
                              const clinic = clinics.find(c => c.id === selectedClinic);
                              return clinic && ` at ${clinic.clinic_name || clinic.name}`;
                            })()}
                          </Typography>
                        </Alert>
                      )}
                    </Box>
                  )}

                  {activeStep === 2 && (
                    <Box>
                      <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                        Select Date & Time
                      </Typography>
                      
                      {/* Show selected doctor info */}
                      {selectedDoctor && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                          <Typography variant="body2">
                            <strong>Doctor:</strong>{' '}
                            {(() => {
                              const doctor = getDoctorDetails();
                              return doctor?.user?.name || doctor?.full_name || doctor?.name || doctor?.title || `Doctor ${selectedDoctor}`;
                            })()}
                            {selectedClinic && (() => {
                              const clinic = clinics.find(c => c.id === selectedClinic);
                              return clinic && (
                                <>
                                  {' | '}
                                  <strong>Clinic:</strong> {clinic.clinic_name || clinic.name}
                                </>
                              );
                            })()}
                          </Typography>
                        </Alert>
                      )}
                      
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: isMobile ? 'column' : 'row',
                        gap: 4,
                        alignItems: 'flex-start'
                      }}>
                        <Box sx={{ 
                          flex: 1,
                          '& .react-calendar': {
                            width: '100%',
                            maxWidth: 400,
                            margin: '0 auto',
                          }
                        }}>
                          <StyledCalendar
                            onChange={setSelectedDate}
                            value={selectedDate}
                            tileDisabled={({ date }) => {
                              const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Kolkata";
                              const nowInTimeZone = DateTime.now().setZone(timeZone).startOf("day");
                              const checkDate = DateTime.fromJSDate(date).setZone(timeZone).startOf("day");
                              return checkDate < nowInTimeZone;
                            }}
                          />
                        </Box>

                        {selectedDate && (
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle1" gutterBottom>
                              Available Time Slots
                            </Typography>
                            {loading ? (
                              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                                <CircularProgress />
                                <Typography variant="body2" sx={{ ml: 2, alignSelf: 'center' }}>
                                  Loading slots...
                                </Typography>
                              </Box>
                            ) : available_slots.length > 0 ? (
                              <>
                                <Box sx={{ 
                                  display: 'grid', 
                                  gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                                  gap: 1 
                                }}>
                                  {available_slots.map((slot) => (
                                    <StyledTimeSlot
                                      key={slot}
                                      variant={selectedSlot === slot ? 'contained' : 'outlined'}
                                      onClick={() => setSelectedSlot(slot)}
                                      className={selectedSlot === slot ? 'selected' : ''}
                                    >
                                      {slot}
                                    </StyledTimeSlot>
                                  ))}
                                </Box>
                                
                                {/* Selected slot confirmation */}
                                {selectedSlot && (
                                  <Alert severity="success" sx={{ mt: 2 }}>
                                    <Typography variant="body2">
                                      <strong>Selected:</strong> {selectedDate?.toLocaleDateString('en-US', { 
                                        weekday: 'long',
                                        month: 'short', 
                                        day: 'numeric',
                                        year: 'numeric'
                                      })} at {selectedSlot}
                                    </Typography>
                                  </Alert>
                                )}
                              </>
                            ) : (
                              <Alert severity="warning" sx={{ mt: 2 }}>
                                <AlertTitle>No Slots Available</AlertTitle>
                                <Typography variant="body2">
                                  {selectedDoctor ? 
                                    "No time slots are available for this date. The doctor may be fully booked or not available on this day. Please try selecting a different date." :
                                    "Please select a doctor first to see available time slots."
                                  }
                                </Typography>
                              </Alert>
                            )}
                          </Box>
                        )}
                        
                        {!selectedDate && (
                          <Alert severity="info" sx={{ mt: 2, mx: 2 }}>
                            <Typography variant="body2">
                              Please select a date from the calendar to see available time slots
                            </Typography>
                          </Alert>
                        )}
                      </Box>
                    </Box>
                  )}

                  {activeStep === 3 && (
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h6" gutterBottom>
                        Confirm Your Appointment
                      </Typography>
                      <Box sx={{ mt: 3, mb: 4 }}>
                        <Typography variant="body1" color="textSecondary" paragraph>
                          <strong>Doctor:</strong>{' '}
                          {selectedDoctor && (() => {
                            const doctor = getDoctorDetails();
                            return doctor?.user?.name || doctor?.full_name || doctor?.name || doctor?.title || `Doctor ${selectedDoctor}`;
                          })()}
                        </Typography>
                        {selectedClinic && (
                          <Typography variant="body1" color="textSecondary" paragraph>
                            <strong>Clinic:</strong>{' '}
                            {(() => {
                              const clinic = clinics.find(c => c.id === selectedClinic);
                              return clinic?.clinic_name || clinic?.name || 'Selected Clinic';
                            })()}
                          </Typography>
                        )}
                        <Typography variant="body1" color="primary" sx={{ fontWeight: 600 }}>
                          {selectedDate?.toLocaleDateString('en-US', { 
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </Typography>
                        <Typography variant="body1" color="primary" sx={{ fontWeight: 600 }}>
                          {selectedSlot}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Fade>

              <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
                {activeStep > 0 && (
                  <StyledButton
                    variant="outlined"
                    onClick={handleBack}
                    sx={{ flex: 1 }}
                  >
                    Back
                  </StyledButton>
                )}
                <StyledButton
                  variant="contained"
                  onClick={handleNext}
                  disabled={loading}
                  sx={{ 
                    flex: 1,
                    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : activeStep === steps.length - 1 ? (
                    'Confirm Booking'
                  ) : (
                    'Next'
                  )}
                </StyledButton>
              </Box>
            </Box>
          </StyledPaper>
        </Box>
      </Fade>

      <StyledModal
        open={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={isLoginModalOpen}>
          <StyledPaper>
            <Typography variant="h6" gutterBottom>
              Please Log In
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Your booking details have been saved. Please log in or sign up to confirm your appointment.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <StyledButton
                variant="outlined"
                onClick={() => handleRedirect('/login')}
                fullWidth
              >
                Log In
              </StyledButton>
              <StyledButton
                variant="contained"
                onClick={() => handleRedirect('/signup')}
                fullWidth
                sx={{
                  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                }}
              >
                Sign Up
              </StyledButton>
            </Box>
          </StyledPaper>
        </Fade>
      </StyledModal>

      <ToastContainer 
        position="bottom-center" 
        autoClose={3000}
        theme="colored"
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </Container>
  );
};

export default BookAppointment;
